import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { CheckCircle, ChevronDown, Smartphone, ZoomIn } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { Button } from '../components/ui/button';
import {
  SIGN_LINK_POLL_MS,
  SIGN_LINK_STATUS_RELEASED,
  SIGN_LINK_VISA,
  SignLinkBootstrap,
  SignLinkHttpError,
  fetchSignLink,
  fetchSignLinkStatus,
  formatSignLinkDate,
  formatSignLinkDateTime,
  formatSignerName,
  getSignLinkView,
  mergeSignLinkStatus,
  safeSimaHref,
  signLinkBlockReasonKey,
  signLinkStatusKey,
  startSimaSign,
} from '../lib/signLink';

const QR_ICON_SRC = `${process.env.PUBLIC_URL}/qr-icon.svg`;

function getZoomedQrSize(): number {
  if (typeof window === 'undefined') return 320;
  return Math.floor(Math.min(window.innerWidth, window.innerHeight) * 0.85 - 32);
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'notFound' }
  | { kind: 'invalid' }
  | { kind: 'error' }
  | { kind: 'loaded' };

const primaryButtonClass = 'w-full bg-[#1976d2] text-white hover:bg-[#1565c0]';

const SignLink: React.FC = () => {
  const { token: rawToken } = useParams<{ token?: string }>();
  const token = rawToken?.trim() ?? '';
  const { t } = useLanguage();
  const isDesktop = useIsDesktop();
  const [attempt, setAttempt] = useState(0);
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'loading' });
  const [link, setLink] = useState<SignLinkBootstrap | null>(null);
  const [signing, setSigning] = useState(false);
  const [qrText, setQrText] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLink(null);
      setSigning(false);
      setQrText(null);
      setActionError(null);
      setLoadState({ kind: 'invalid' });
      return undefined;
    }

    let cancelled = false;
    setLoadState({ kind: 'loading' });
    setLink(null);
    setSigning(false);
    setQrText(null);
    setActionError(null);

    const load = async () => {
      try {
        const data = await fetchSignLink(token);
        if (cancelled) return;
        setLink(data);
        setLoadState({ kind: 'loaded' });
      } catch (error) {
        if (cancelled) return;
        if (error instanceof SignLinkHttpError && error.status === 404) {
          setLoadState({ kind: 'notFound' });
          return;
        }
        if (error instanceof SignLinkHttpError && error.status === 400) {
          setLoadState({ kind: 'invalid' });
          return;
        }
        setLoadState({ kind: 'error' });
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [token, attempt]);

  const view = link ? getSignLinkView(link, signing && Boolean(qrText)) : null;

  useEffect(() => {
    if (!token || view !== 'signing') return undefined;

    let stopped = false;
    let inFlight = false;

    const tick = async () => {
      if (stopped || inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        const next = await fetchSignLinkStatus(token);
        if (!stopped) {
          setLink((current) => (current ? mergeSignLinkStatus(current, next) : current));
        }
      } catch (error) {
        if (stopped) return;
        if (error instanceof SignLinkHttpError && error.status === 404) {
          setLink(null);
          setLoadState({ kind: 'notFound' });
          return;
        }
        if (error instanceof SignLinkHttpError && error.status === 400) {
          setLink(null);
          setLoadState({ kind: 'invalid' });
        }
      } finally {
        inFlight = false;
      }
    };

    const intervalId = window.setInterval(() => {
      void tick();
    }, SIGN_LINK_POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') void tick();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      stopped = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [token, view]);

  const handleSign = async () => {
    if (!token || !link?.canSign || starting) return;
    setStarting(true);
    setActionError(null);
    try {
      const result = await startSimaSign(token);
      if (result.isDocumentLocked) {
        setActionError(result.lockReason || t('signLinkLockGeneric'));
        return;
      }
      if (!result.qrText) {
        setActionError(t('signLinkStartError'));
        return;
      }
      setQrText(result.qrText);
      setSigning(true);
    } catch (error) {
      if (error instanceof SignLinkHttpError && error.status === 404) {
        setLink(null);
        setLoadState({ kind: 'notFound' });
        return;
      }
      if (error instanceof SignLinkHttpError && error.status === 400) {
        setLink(null);
        setLoadState({ kind: 'invalid' });
        return;
      }
      setActionError(t('signLinkStartError'));
    } finally {
      setStarting(false);
    }
  };

  if (loadState.kind === 'loading') {
    return <SignLinkSkeleton label={t('signLinkLoading')} />;
  }

  if (loadState.kind === 'notFound' || loadState.kind === 'invalid') {
    const title = loadState.kind === 'notFound' ? t('signLinkNotFoundTitle') : t('signLinkInvalidTitle');
    const body = loadState.kind === 'notFound' ? t('signLinkNotFoundBody') : t('signLinkInvalidBody');
    return (
      <section className="rounded-card border bg-card p-4 shadow-sm sm:p-6">
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-3 text-sm leading-6 text-neutral-700">{body}</p>
      </section>
    );
  }

  if (loadState.kind === 'error' || !link || !view) {
    return (
      <section className="rounded-card border bg-card p-4 shadow-sm sm:p-6">
        <h1 className="text-xl font-semibold tracking-tight">{t('signLinkLoadError')}</h1>
        <Button type="button" className={`mt-5 ${primaryButtonClass}`} size="lg" onClick={() => setAttempt((value) => value + 1)}>
          {t('signLinkRetry')}
        </Button>
      </section>
    );
  }

  const signerName = formatSignerName(link.signerFirstName, link.signerLastName);
  const validUntil = formatSignLinkDate(link.validTo);
  const signedOn = formatSignLinkDateTime(link.signedAt);
  const isVisa = link.signerType === SIGN_LINK_VISA;
  const blockReasonKey = link.blockReason ? signLinkBlockReasonKey(link.blockReason) : null;
  const blockReasonText = blockReasonKey ? t(blockReasonKey) : link.blockReason;
  const pageTitle =
    link.documentTitle ||
    (isVisa ? t('signLinkPageTitleVisa') : t('signLinkPageTitleSigner'));

  return (
    <section className="rounded-card border bg-card p-4 shadow-sm sm:p-6" aria-live="polite">
      <div className="space-y-3">
        <p>
          <span className="sr-only">{t('signLinkStatusLabel')}: </span>
          <StatusBadge status={link.status} label={t(signLinkStatusKey(link.status))} />
        </p>
        {signerName && (
          <p className="text-sm text-neutral-600">
            {t('signLinkHello')} {signerName}
          </p>
        )}
        <h1 className="break-words text-xl font-semibold tracking-tight sm:text-2xl">{pageTitle}</h1>
        {link.operationId && (
          <p className="break-all text-sm text-neutral-600">
            <span className="font-medium text-neutral-800">{t('operationId')}: </span>
            {link.operationId}
          </p>
        )}
        {validUntil && (
          <p className="text-sm text-neutral-600">
            <span className="font-medium text-neutral-800">{t('signLinkValidUntil')}: </span>
            {validUntil}
          </p>
        )}
      </div>

      {link.blockReason && (
        <p className="mt-4 rounded-xl border border-[#dc004e]/20 bg-[#dc004e]/5 px-3 py-3 text-sm leading-6 text-neutral-800" role="status">
          {blockReasonText}
        </p>
      )}

      {actionError && (
        <p className="mt-4 rounded-xl border border-[#dc004e]/20 bg-[#dc004e]/5 px-3 py-3 text-sm leading-6 text-neutral-800" role="alert">
          {actionError}
        </p>
      )}

      <div className="mt-5">
        {view === 'signing' && qrText ? (
          <SigningPanel qrText={qrText} isDesktop={isDesktop} />
        ) : view === 'completed' ? (
          <CompletedPanel
            signed={link.status !== SIGN_LINK_STATUS_RELEASED}
            signedOn={signedOn}
          />
        ) : isVisa ? (
          <VisaLoginPrompt />
        ) : (
          <div className="space-y-3">
            {view === 'blocked' && !link.blockReason && (
              <p className="text-sm text-neutral-700">{t('signLinkBlockedHint')}</p>
            )}
            <Button
              type="button"
              size="lg"
              className={primaryButtonClass}
              disabled={!link.canSign || starting}
              onClick={() => {
                void handleSign();
              }}
            >
              {starting ? t('signLinkLoading') : t('signLinkSignCta')}
            </Button>
          </div>
        )}
      </div>

      {(view === 'ready' || view === 'signing') && !isVisa && <HowToSign />}

      {!isVisa && <ViewOnWeb />}
    </section>
  );
};

const StatusBadge: React.FC<{ status: number; label: string }> = ({ status, label }) => {
  const tone =
    status === 0
      ? 'border-[#dc004e]/30 bg-[#dc004e]/10 text-[#dc004e]'
      : status === 1 || status === 2
        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
        : status === -1
          ? 'border-red-200 bg-red-50 text-red-800'
          : 'border-neutral-200 bg-neutral-100 text-neutral-700';

  return (
    <span className={`inline-flex min-h-8 items-center rounded-full border px-3 py-1 text-sm font-semibold ${tone}`}>
      {label}
    </span>
  );
};

const SigningPanel: React.FC<{ qrText: string; isDesktop: boolean }> = ({ qrText, isDesktop }) => {
  const { t } = useLanguage();
  const href = safeSimaHref(qrText);
  const openSima = href ? (
    <Button asChild size="lg" className={isDesktop ? 'w-full' : primaryButtonClass} variant={isDesktop ? 'outline' : 'default'}>
      <a href={href}>
        <Smartphone className="h-5 w-5" aria-hidden />
        {t('signLinkOpenSima')}
      </a>
    </Button>
  ) : null;

  const qr = <SimaQr qrText={qrText} />;

  return (
    <div className="space-y-4">
      <h2 className="text-center text-lg font-semibold">{t('signLinkContinueInSima')}</h2>
      {isDesktop ? (
        <>
          {qr}
          {openSima}
        </>
      ) : (
        <>
          {openSima}
          {href ? (
            <details className="group rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-1">
              <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between text-sm font-semibold text-[#1976d2] [&::-webkit-details-marker]:hidden">
                {t('signLinkScanOtherDevice')}
                <ChevronDown className="h-4 w-4 transition group-open:rotate-180" aria-hidden />
              </summary>
              <div className="pb-3">{qr}</div>
            </details>
          ) : (
            qr
          )}
        </>
      )}
      <p className="flex items-center justify-center gap-2 text-center text-sm text-neutral-600" role="status">
        <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-[#1976d2] border-t-transparent" aria-hidden />
        {t('signLinkPolling')}
      </p>
    </div>
  );
};

const SimaQr: React.FC<{ qrText: string }> = ({ qrText }) => {
  const { t } = useLanguage();
  const [qrZoomed, setQrZoomed] = useState(false);
  const [zoomedQrSize, setZoomedQrSize] = useState(getZoomedQrSize);

  useEffect(() => {
    if (!qrZoomed) return undefined;

    const updateZoomSize = () => setZoomedQrSize(getZoomedQrSize());
    updateZoomSize();
    window.addEventListener('resize', updateZoomSize);
    return () => window.removeEventListener('resize', updateZoomSize);
  }, [qrZoomed]);

  useEffect(() => {
    if (!qrZoomed) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setQrZoomed(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [qrZoomed]);

  const qrCode = (size: number) => {
    const iconSize = Math.max(36, Math.round(size * 0.22));

    return (
      <QRCodeSVG
        value={qrText}
        size={size}
        level="H"
        includeMargin
        imageSettings={{
          src: QR_ICON_SRC,
          height: iconSize,
          width: iconSize,
          excavate: true,
        }}
        className="mx-auto h-auto w-full max-w-full"
      />
    );
  };

  return (
    <>
      <div className="mx-auto w-full max-w-[280px]">
        <button
          type="button"
          onClick={() => setQrZoomed(true)}
          className="group w-full rounded-xl border bg-white p-3 text-left transition hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label={t('simaEnlargeQr')}
        >
          <div role="img" aria-label={t('signLinkQrAlt')} className="flex justify-center">
            {qrCode(240)}
          </div>
          <span className="mt-2 flex items-center justify-center gap-1 text-xs text-neutral-600 group-hover:text-neutral-900">
            <ZoomIn className="h-3.5 w-3.5" aria-hidden />
            {t('simaTapQrToZoom')}
          </span>
        </button>
        <p className="mt-2 text-center text-sm leading-5 text-neutral-600">{t('signLinkQrInstructions')}</p>
      </div>
      {qrZoomed &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/90 p-4"
            onClick={() => setQrZoomed(false)}
            role="dialog"
            aria-modal="true"
            aria-label={t('simaEnlargedQr')}
          >
            <div className="bg-white p-4 shadow-lg" onClick={(event) => event.stopPropagation()}>
              <div role="img" aria-label={t('signLinkQrAlt')}>
                {qrCode(zoomedQrSize)}
              </div>
            </div>
            <p className="mt-4 text-center text-sm text-white/80">{t('simaTapToCloseQr')}</p>
          </div>,
          document.body,
        )}
    </>
  );
};

const CompletedPanel: React.FC<{ signed: boolean; signedOn: string | null }> = ({ signed, signedOn }) => {
  const { t } = useLanguage();
  return (
    <div className="flex flex-col items-center gap-2 py-2 text-center">
      <CheckCircle className="h-12 w-12 text-emerald-600" aria-hidden />
      <p className="text-base font-semibold">{t('signLinkThanks')}</p>
      <p className="text-sm text-neutral-700">{signed ? t('signLinkCompletedSigned') : t('signLinkCompletedReleased')}</p>
      {signedOn && (
        <p className="text-sm text-neutral-600">
          <span className="font-medium text-neutral-800">{t('signLinkSignedOn')}: </span>
          {signedOn}
        </p>
      )}
    </div>
  );
};

const HowToSign: React.FC = () => {
  const { t } = useLanguage();
  return (
    <details className="group mt-5 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-1">
      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between text-sm font-semibold [&::-webkit-details-marker]:hidden">
        {t('signLinkHowToSignTitle')}
        <ChevronDown className="h-4 w-4 text-neutral-500 transition group-open:rotate-180" aria-hidden />
      </summary>
      <ol className="list-decimal space-y-2 pb-3 pl-5 text-sm leading-6 text-neutral-700">
        <li>{t('signLinkHowToStep1')}</li>
        <li>{t('signLinkHowToStep2')}</li>
        <li>{t('signLinkHowToStep3')}</li>
      </ol>
    </details>
  );
};

const VisaLoginPrompt: React.FC = () => {
  const { t } = useLanguage();
  return (
    <div className="space-y-4">
      <p className="text-sm leading-6 text-neutral-700">{t('signLinkVisaNotice')}</p>
      <Button asChild size="lg" className={primaryButtonClass}>
        <Link to="/login">{t('signLinkVisaCta')}</Link>
      </Button>
    </div>
  );
};

const ViewOnWeb: React.FC = () => {
  const { t } = useLanguage();
  return (
    <p className="mt-5 border-t pt-4 text-sm leading-6 text-neutral-700">
      {t('signLinkViewIntro')}{' '}
      <Link to="/login" className="font-semibold text-[#1976d2] underline underline-offset-2">
        {t('signLinkLoginAction')}
      </Link>{' '}
      {t('signLinkViewOr')}{' '}
      <Link to="/register" className="font-semibold text-[#1976d2] underline underline-offset-2">
        {t('signLinkRegisterAction')}
      </Link>.
    </p>
  );
};

const SignLinkSkeleton: React.FC<{ label: string }> = ({ label }) => (
  <div className="animate-pulse rounded-card border bg-card p-4 shadow-sm sm:p-6" aria-busy="true" aria-live="polite">
    <span className="sr-only">{label}</span>
    <div className="h-7 w-28 rounded-full bg-neutral-200" />
    <div className="mt-4 h-4 w-24 rounded bg-neutral-200" />
    <div className="mt-3 h-8 w-4/5 rounded bg-neutral-200" />
    <div className="mt-3 h-4 w-1/2 rounded bg-neutral-200" />
    <div className="mt-6 h-14 w-full rounded-full bg-neutral-200" />
  </div>
);

export default SignLink;
